import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ClipboardCheck, 
  Maximize2, 
  Loader2, 
  AlertTriangle, 
  CheckCircle2, 
  Clock,
  Sun,
  Moon,
  Shield
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';

export default function ChecklistComplianceWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchWidgetData();
  }, []);

  const fetchWidgetData = async () => {
    try {
      const result = await apiRequest('/store-checklists/widget');
      setData(result);
    } catch (error) {
      console.error('Failed to load checklist widget:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="text-center text-slate-500 py-8">
          Unable to load checklist data
        </div>
      </div>
    );
  }

  const { 
    total_stores, 
    stores_completed, 
    stores_partial, 
    stores_missing,
    completion_rate,
    opening_rate,
    closing_rate,
    store_breakdown,
    escalations
  } = data;

  // Determine overall status color
  const getStatusColor = (rate) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'complete': return 'bg-green-100 text-green-700';
      case 'partial': return 'bg-amber-100 text-amber-700';
      case 'missing': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 h-full" data-testid="checklist-compliance-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-indigo-600" />
          <h3 className="font-semibold text-slate-900">Today's Checklists</h3>
        </div>
        {expandable && expandPath && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(expandPath)}
            className="h-8 w-8 p-0"
            data-testid="checklist-widget-expand"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Overall Completion Rate */}
        <div className="text-center">
          <div className={`text-3xl font-bold ${getStatusColor(completion_rate)}`}>
            {completion_rate}%
          </div>
          <div className="text-sm text-slate-500">Stores Fully Compliant</div>
        </div>

        {/* Type Breakdown */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 bg-orange-50 rounded-lg">
            <Sun className="h-4 w-4 text-orange-500 mx-auto mb-1" />
            <div className={`text-lg font-semibold ${getStatusColor(opening_rate)}`}>
              {opening_rate}%
            </div>
            <div className="text-xs text-slate-500">Opening</div>
          </div>
          <div className="p-2 bg-indigo-50 rounded-lg">
            <Moon className="h-4 w-4 text-indigo-500 mx-auto mb-1" />
            <div className={`text-lg font-semibold ${getStatusColor(closing_rate)}`}>
              {closing_rate}%
            </div>
            <div className="text-xs text-slate-500">Closing</div>
          </div>
          <div className="p-2 bg-green-50 rounded-lg">
            <Shield className="h-4 w-4 text-green-500 mx-auto mb-1" />
            <div className={`text-lg font-semibold ${getStatusColor(data.compliance_rate)}`}>
              {data.compliance_rate}%
            </div>
            <div className="text-xs text-slate-500">Compliance</div>
          </div>
        </div>

        {/* Store Status Summary */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span className="text-green-700 font-medium">{stores_completed}</span>
            <span className="text-slate-400">done</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-amber-500" />
            <span className="text-amber-700 font-medium">{stores_partial}</span>
            <span className="text-slate-400">partial</span>
          </div>
          <div className="flex items-center gap-1">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <span className="text-red-700 font-medium">{stores_missing}</span>
            <span className="text-slate-400">missing</span>
          </div>
        </div>

        {/* Escalations Alert */}
        {escalations.open > 0 && (
          <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-100">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <span className="text-sm text-red-700 font-medium">
              {escalations.open} escalation{escalations.open > 1 ? 's' : ''} open
            </span>
          </div>
        )}

        {/* Store Breakdown (Top 5 needing attention) */}
        {store_breakdown.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-medium text-slate-500 mb-2">Store Status</div>
            <div className="space-y-1">
              {store_breakdown.slice(0, 5).map((store) => (
                <div key={store.store} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 truncate max-w-[120px]">{store.store}</span>
                  <div className="flex items-center gap-1">
                    {store.opening ? (
                      <Sun className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <Sun className="h-3.5 w-3.5 text-slate-300" />
                    )}
                    {store.closing ? (
                      <Moon className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <Moon className="h-3.5 w-3.5 text-slate-300" />
                    )}
                    {store.compliance ? (
                      <Shield className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <Shield className="h-3.5 w-3.5 text-slate-300" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View All Link */}
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full mt-2"
          onClick={() => navigate('/checklists/compliance')}
          data-testid="checklist-widget-view-all"
        >
          View Full Dashboard
        </Button>
      </div>
    </div>
  );
}
